import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import "react-notifications/lib/notifications.css";
import Header from "../components/Header/header";
import SecondHeader from "../components/Header/secondheader";
import Footer from "../components/Footer/index";
import { LanguageSwitcher } from "../components/shared";
import { isRegistrationOpen } from "../utils/tinkerpreneur";
import { state } from "../components/DataJson/IndianStates.json";
import { useTranslation } from "react-i18next";

const YEAR = 2025;
const LANGUAGE_OPTIONS = [
  "English",
  "Hindi",
  "Bengali",
  "Tamil",
  "Telugu",
  "Oriya",
  "Malayalam",
  "Marathi",
  "Kannada",
  "Gujarati",
];

const createRegistrationNotification = (type) => {
  const notifications = {
    register_success: () =>
      NotificationManager.success(
        "User details saved!",
        `Congratulations! You have successfully registered for the India AI Tinkerpreneur ${YEAR}.`,
        3000
      ),
    default: () =>
      NotificationManager.error(
        "Please try again or come back later.",
        "Uh-oh, there was an error!",
        6000
      ),
  };

  return (notifications[type] || notifications.default)();
};

const fetchStudentData = async (token) => {
  const headers = { "x-access-token": token };
  const id = localStorage.getItem("studentId");

  const [studentResponse, registrationResponse] = await Promise.all([
    axios.get(`${process.env.REACT_APP_API_URL}/users/students/${id}`, {
      headers,
    }),
    axios.get(
      `${process.env.REACT_APP_API_URL}/atl-tinkerpreneur/has-registered`,
      { headers }
    ),
  ]);

  return {
    studentData: studentResponse.data.data[0],
    registrationData: registrationResponse.data?.studentRegistration[0],
    hasRegistered: registrationResponse.data.hasRegistered,
  };
};

const ReadOnlyField = ({ label, value, name, t, translationPath }) => (
  <div className="form-group">
    <label htmlFor={name}>{t(`${translationPath}.${label}`)}</label>
    <Field
      type="text"
      name={name}
      className="form-control bg-light m-0"
      id={name}
      placeholder={value}
      readOnly
    />
  </div>
);

const PhoneNumberField = ({ hasRegistered, t, translationPath }) => (
  <div className="form-group">
    <label htmlFor="contact_number">
      {t(`${translationPath}.phone_label`)}
    </label>
    <Field name="contact_number">
      {({ field }) => (
        <input
          {...field}
          type="text"
          id="contact_number"
          className={`form-control ${hasRegistered && "bg-light m-0"}`}
          maxLength={10}
          onInput={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "");
          }}
          readOnly={hasRegistered}
        />
      )}
    </Field>
    <ErrorMessage
      name="contact_number"
      component="div"
      className="text-danger"
    />
  </div>
);

const LanguageField = ({
  name,
  translationPath,
  hasRegistered,
  selectedLanguage,
  t,
  setSelectedLanguage,
  setFieldValue,
  customLanguage,
  setCustomLanguage,
  defaultValue,
}) => (
  <div className="form-group">
    <label htmlFor={name}>{t(`${translationPath}.${name}_label`)}</label>

    {!defaultValue && (
      <Field
        as="select"
        name={name}
        className="form-control my-0 ml-0"
        onChange={(e) => {
          const selectedValue = e.target.value;
          setSelectedLanguage(selectedValue);
          setFieldValue(name, selectedValue);
          if (selectedValue !== "Others") {
            setCustomLanguage("");
          }
        }}
        disabled={hasRegistered}
      >
        <option value="">{t(`${translationPath}.select_lanugage`)}</option>
        {LANGUAGE_OPTIONS.map((lang) => (
          <option key={lang} value={lang}>
            {lang}
          </option>
        ))}
        <option value="Others">Others</option>
      </Field>
    )}

    {(defaultValue || selectedLanguage === "Others") && (
      <Field name={`${name}_custom`}>
        {({ field, form }) => (
          <input
            type="text"
            className="form-control mt-2  bg-light m-0"
            value={customLanguage}
            placeholder={defaultValue}
            onChange={(e) => {
              const value = e.target.value;
              setCustomLanguage(value);
              form.setFieldValue(name, value);
            }}
            disabled={hasRegistered}
          />
        )}
      </Field>
    )}

    <ErrorMessage name={name} component="div" className="text-danger" />
  </div>
);

const RadioFieldGroup = ({
  name,
  hasRegistered,
  required,
  t,
  translationPath,
}) => (
  <div className="form-group">
    <label className="d-block">
      {t(`${translationPath}.${name}_label`)}
      {required && <small className="text-danger">*</small>}
    </label>

    <div className="mt-2">
      <div className="form-check form-check-inline">
        <Field
          type="radio"
          name={name}
          value="Yes"
          id={`${name}-yes`}
          className="form-check-input"
          disabled={hasRegistered}
        />
        <label className="form-check-label" htmlFor={`${name}-yes`}>
          {t("submission_page_special_award_option_yes")}
        </label>
      </div>
      <div className="form-check form-check-inline">
        <Field
          type="radio"
          name={name}
          value="No"
          id={`${name}-no`}
          className="form-check-input"
          disabled={hasRegistered}
        />
        <label className="form-check-label" htmlFor={`${name}-no`}>
          {t("submission_page_special_award_option_no")}
        </label>
      </div>
    </div>
    <ErrorMessage name={name} component="div" className="text-danger" />
  </div>
);

const SubmitButton = ({ isSubmitting, hasRegistered, t, translationPath }) =>
  !hasRegistered && (
    <button
      type="submit"
      className="btn btn-primary mr-3 mt-3 mt-md-0"
      disabled={isSubmitting}
    >
      {isSubmitting && (
        <span
          className="spinner-grow spinner-grow-sm mr-2"
          role="status"
          aria-hidden="true"
        ></span>
      )}
      {isSubmitting
        ? t(`${translationPath}.submitting`)
        : t(`${translationPath}.confirm_button_text`)}
    </button>
  );

const RegistrationForm = ({
  onSubmit,
  hasRegistered,
  stateLabel,
  data,
  t,
  studentName,
  email,
}) => {
  const [primaryLanguageState, setPrimaryLanguageState] = useState("");
  const [secondaryLanguageState, setSecondaryLanguageState] = useState("");
  const { i18n } = useTranslation();

  const translationPath = "atl_tinkerpreneur.registration_form";
  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      contact_number: Yup.string()
        .matches(
          /^[6-9]\d{9}$/,
          t(`${translationPath}.validation.mobile_invalid`)
        )
        .required(t(`${translationPath}.validation.mobile_required`)),
      primary_language: Yup.string().required(
        t(`${translationPath}.validation.primary_language`)
      ),
      secondary_language: Yup.string().required(
        t(`${translationPath}.validation.secondary_language`)
      ),
      want_to_up_skill: Yup.string().required(
        t(`${translationPath}.validation.want_to_up_skill`)
      ),
      email_sharable: Yup.string().required(
        t(`${translationPath}.validation.email_sharable`)
      ),
    });
  }, [t, translationPath]);

  return (
    <Formik
      key={i18n.language}
      enableReinitialize
      initialValues={{
        contact_number: data.contact_number || "",
        experience_in_ai: data.experience_in_ai || null,
        want_to_up_skill: data.want_to_up_skill || null,
        email_sharable: data.email_sharable || null,
        primary_language: data.primary_language || "",
        secondary_language: data.secondary_language || "",
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, setFieldValue }) => (
        <Form>
          <div className="form-row">
            <div className="col-md-6 px-3 mb-3">
              <ReadOnlyField
                label="student_name_label"
                value={studentName}
                t={t}
                name="studentName"
                translationPath={translationPath}
              />
            </div>
            <div className="col-md-6 px-3 mb-3">
              <ReadOnlyField
                label="state_label"
                value={stateLabel}
                t={t}
                name="state"
                translationPath={translationPath}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="col-md-6 px-3 mb-3">
              <ReadOnlyField
                label="email_label"
                value={email}
                t={t}
                name="email"
                translationPath={translationPath}
              />
            </div>
            <div className="col-md-6 px-3 mb-3">
              <PhoneNumberField
                hasRegistered={hasRegistered}
                t={t}
                translationPath={translationPath}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="col-md-6 px-3 mb-3">
              <LanguageField
                name="primary_language"
                translationPath={translationPath}
                hasRegistered={hasRegistered}
                t={t}
                selectedLanguage={primaryLanguageState.selected}
                setSelectedLanguage={(val) =>
                  setPrimaryLanguageState((prev) => ({
                    ...prev,
                    selected: val,
                  }))
                }
                defaultValue={data.primary_language}
                setFieldValue={setFieldValue}
                customLanguage={primaryLanguageState.custom}
                setCustomLanguage={(val) =>
                  setPrimaryLanguageState((prev) => ({ ...prev, custom: val }))
                }
              />
            </div>
            <div className="col-md-6 px-3 mb-3">
              <LanguageField
                name="secondary_language"
                translationPath={translationPath}
                hasRegistered={hasRegistered}
                t={t}
                selectedLanguage={secondaryLanguageState.selected}
                setSelectedLanguage={(val) =>
                  setSecondaryLanguageState((prev) => ({
                    ...prev,
                    selected: val,
                  }))
                }
                defaultValue={data.secondary_language}
                setFieldValue={setFieldValue}
                customLanguage={secondaryLanguageState.custom}
                setCustomLanguage={(val) =>
                  setSecondaryLanguageState((prev) => ({
                    ...prev,
                    custom: val,
                  }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="col-md-12 mb-3 px-3 mb-md-0">
              <RadioFieldGroup
                name="experience_in_ai"
                hasRegistered={hasRegistered}
                t={t}
                translationPath={translationPath}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="col-md-12 mb-3 px-3 mb-md-0">
              <RadioFieldGroup
                required
                name="want_to_up_skill"
                hasRegistered={hasRegistered}
                t={t}
                translationPath={translationPath}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="col-md-12 mb-3 px-3 mb-md-0">
              <RadioFieldGroup
                required
                name="email_sharable"
                hasRegistered={hasRegistered}
                t={t}
                translationPath={translationPath}
              />
            </div>
          </div>

          <SubmitButton
            isSubmitting={isSubmitting}
            hasRegistered={hasRegistered}
            t={t}
            translationPath={translationPath}
          />
        </Form>
      )}
    </Formik>
  );
};

const RegistrationClosed = ({ t, year }) => (
  <div className="iaiif-registration-page gray-light-bg footer-sticky-bottom">
    <div className="blog-container shadow">
      <div className="container">
        <div className="row align-items-center user-info-container">
          <div className="col-md-12">
            <h1 className="text-center">
              {t("submission_page_title").replace("{year}", year)}
            </h1>
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-12 col-md-9 col-xl-7">
            <div className="alert alert-info mx-auto my-3 my-md-5">
              {t("atl_tinkerpreneur.registration_form.registration_closed")}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ATLTinkerpreneurRegistration2025 = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [className, setClassName] = useState("6");
  const [district, setDistrict] = useState(null);
  const [hasRegistered, setRegistered] = useState(false);
  const token = localStorage.getItem("jwtToken");
  const { t } = useTranslation();

  const fetchAllData = async () => {
    try {
      const { studentData, registrationData, hasRegistered } =
        await fetchStudentData(token);

      setRegistered(hasRegistered);
      setClassName(registrationData?.class || studentData?.class);
      setDistrict(registrationData?.district || studentData?.district);
      setData({ ...studentData, ...registrationData });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleRegistration = async (values, setSubmitting) => {
    const headers = { "x-access-token": token };
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/atl-tinkerpreneur/register`,
        { ...values, class: className, district },
        { headers }
      );
      createRegistrationNotification("register_success");
    } catch (error) {
      createRegistrationNotification("error");
    } finally {
      setSubmitting(false);
      fetchAllData();
    }
  };

  const stateLabel = state.find((opt) => opt.key === data?.state)?.value || "";
  const studentName = `${data.first_name} ${data.last_name}`;

  if (!isRegistrationOpen(YEAR)) {
    return (
      <>
        {token ? <SecondHeader /> : <Header />}
        <RegistrationClosed year={YEAR} />
        <Footer />
      </>
    );
  }

  return (
    <>
      {token ? <SecondHeader /> : <Header />}
      <div className="iaiif-registration-page gray-light-bg">
        <div className="container d-flex justify-content-end">
          <LanguageSwitcher />
        </div>
        <div className="blog-container shadow">
          <div className="container">
            <div className="row user-info-container">
              <div className="col-md-12 text-center">
                <img
                  src="/images/ai-tinkerpreneur-logo.png"
                  alt="AI Tinkerpreneur Logo"
                  className="mb-3 img-fluid"
                  style={{ maxWidth: "200px" }}
                />
                <h1 className="text-center">
                  {t("atl_tinkerpreneur.registration_form.title").replace(
                    "{year}",
                    YEAR
                  )}
                </h1>
                <h2 className="text-center">
                  {t("atl_tinkerpreneur.registration_form.description")}
                </h2>
              </div>
            </div>

            {loading ? (
              <div className="d-flex align-items-center justify-content-center py-5">
                <div className="spinner-grow" role="status">
                  <span className="sr-only">{t("loading_text")}</span>
                </div>
              </div>
            ) : (
              <div>
                {hasRegistered && (
                  <div className="alert alert-success">
                    {t(
                      "atl_tinkerpreneur.registration_form.confirmed_text"
                    ).replace("{year}", YEAR)}
                  </div>
                )}

                <RegistrationForm
                  data={data}
                  onSubmit={(values, { setSubmitting }) => {
                    handleRegistration(values, setSubmitting);
                  }}
                  t={t}
                  hasRegistered={hasRegistered}
                  stateLabel={stateLabel}
                  studentName={studentName}
                  email={data.email_id}
                  className={className}
                  setClassName={setClassName}
                  district={district}
                  setDistrict={setDistrict}
                />
              </div>
            )}
          </div>
        </div>
        <NotificationContainer />
      </div>
      <Footer />
    </>
  );
};

export default ATLTinkerpreneurRegistration2025;
